import { getPenColoursSet } from "@brickme/project-core/src/model/picture.ts";
import type { AppState } from "~/store/root-reducer.ts";
import {
	isFrameCompatibleWithProject,
	getFrameVariantForPicture,
} from "~/features/preview/frame.ts";
import { isOnceOffLoaded } from "~/utils/loading.ts";
import { createSelector } from "@reduxjs/toolkit";

function selectOptionalOpenProject(state: AppState) {
	return state.workspace.openProject;
}

function selectOpenProject(state: AppState) {
	const openProject = selectOptionalOpenProject(state);
	if (!openProject) {
		throw new Error("No workspace project");
	}
	return openProject;
}

function selectOpenProjectProject(state: AppState) {
	return selectOpenProject(state).project;
}

function selectHasOpenProject(state: AppState) {
	return !!state.workspace.openProject;
}

function selectCurrentPictureConfig(state: AppState) {
	return selectOpenProjectProject(state).currentPicture;
}

function selectCurrentPictureOutput(state: AppState) {
	return selectOpenProject(state).build;
}

function selectCurrentPictureBrickCounts(state: AppState) {
	return selectOpenProject(state).build.brickCounts;
}

function selectCurrentPictureHasPenMarks(state: AppState) {
	const p = selectCurrentPictureConfig(state);
	return getPenColoursSet(p.pen).size > 0;
}

function selectCurrentPicturePaletteMode(state: AppState) {
	return selectCurrentPictureConfig(state).paletteMode;
}

function selectCurrentPictureDithering(state: AppState) {
	return selectCurrentPictureConfig(state).dithering;
}

function selectCurrentPictureDetailFilter(state: AppState) {
	return selectCurrentPictureConfig(state).detailFilter;
}

function selectCurrentPictureImageZoom(state: AppState) {
	return selectCurrentPictureConfig(state).imageZoom;
}

function selectCurrentPictureBasePlateSize(state: AppState) {
	return selectCurrentPictureConfig(state).basePlateSize;
}

function selectCurrentPictureFixFaceColours(state: AppState) {
	return selectCurrentPictureConfig(state).fixFaceColours;
}

function selectCurrentPicturePosterise(state: AppState) {
	return selectCurrentPictureConfig(state).posterise;
}

function selectCurrentPictureRemoveBackground(state: AppState) {
	return selectCurrentPictureConfig(state).removeBackground;
}

function selectNumberOfPaletteColours(state: AppState) {
	const paletteMode = selectCurrentPicturePaletteMode(state);
	if (paletteMode.type === "auto") {
		return paletteMode.numberOfColours;
	}
	const counts = selectCurrentPictureOutput(state).brickCounts;
	return Object.values(counts).filter((c) => c.count > 0).length;
}

function selectCurrentPictureNumberOfBasePlates(state: AppState) {
	return selectCurrentPictureConfig(state).numberOfBasePlates;
}

function selectOpenProjectBuiltCurrentPictureUpdatedAt(state: AppState) {
	return state.workspace.openProject?.builtCurrentPictureUpdatedAt;
}

function selectFrames(state: AppState) {
	return state.reference.frames;
}

const selectCurrentPictureCompatibleFrames = createSelector(
	[selectCurrentPictureConfig, selectFrames],
	(picture, frames) => {
		const allFrames = isOnceOffLoaded(frames) ? frames.data : [];
		return allFrames.filter((f) => isFrameCompatibleWithProject(f, picture));
	},
);

const selectCurrentPictureCompatibleFramesWithInStockPrice = createSelector(
	[selectCurrentPictureConfig, selectCurrentPictureCompatibleFrames],
	(picture, compatibleFrames) => {
		return compatibleFrames.filter(
			(f) => getFrameVariantForPicture(picture, f).inStock,
		);
	},
);

export {
	selectCurrentPictureBasePlateSize,
	selectOpenProjectBuiltCurrentPictureUpdatedAt,
	selectCurrentPictureCompatibleFrames,
	selectCurrentPictureCompatibleFramesWithInStockPrice,
	selectOptionalOpenProject,
	selectCurrentPicturePosterise,
	selectCurrentPictureNumberOfBasePlates,
	selectNumberOfPaletteColours,
	selectCurrentPictureDetailFilter,
	selectCurrentPicturePaletteMode,
	selectCurrentPictureHasPenMarks,
	selectCurrentPictureBrickCounts,
	selectCurrentPictureOutput,
	selectOpenProject,
	selectOpenProjectProject,
	selectHasOpenProject,
	selectCurrentPictureConfig,
	selectCurrentPictureFixFaceColours,
	selectCurrentPictureRemoveBackground,
	selectCurrentPictureImageZoom,
	selectCurrentPictureDithering,
};
